import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { identifyComponent } from '../../util';
import { Avatar, sizeToAppearanceMap } from '../Avatar/Avatar';
import avatarSizeClasses from '../Avatar/sizes.module.scss';
import { List } from '../List';
import { ListItem } from '../ListItem';
import { ListItemText } from '../ListItemText';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import classes from './AvatarGroup.module.scss';
import { AvatarArray, AvatarGroupComponentProps } from './types';

export const AvatarList: React.FunctionComponent<{ avatars: AvatarArray }> = ({ avatars = [] }) => (
  <List data-component="AvatarList" disableGutters disablePadding compact>
    {avatars.map(({ id, name, subtitle }) => (
      <ListItem key={id}>
        <Avatar size="sm" name={name} />
        <ListItemText text={name} subtext={subtitle} />
      </ListItem>
    ))}
  </List>
);

export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupComponentProps>(
  function AvatarGroup({ size = 'md', overflowAfter = 5, avatars, ...nativeProps }, ref) {
    const overflow = avatars.slice(overflowAfter);
    const visible = avatars.slice(0, overflowAfter);

    return (
      <div
        ref={ref}
        className={classes.root}
        data-component="AvatarGroup"
        data-testid="avatar-group"
        {...nativeProps}
        {...identifyComponent('avatar-group', nativeProps)}
      >
        {visible.map(({ id, name, subtitle }, index) => (
          <div
            key={id}
            className={classNames(classes.avatarWrapper, {
              [classes[`offset-${size}` as const]]: index !== 0,
            })}
          >
            <Avatar size={size} name={name} subtitle={subtitle} />
          </div>
        ))}
        {overflow.length > 0 && (
          <Tooltip
            asChild
            placement="bottom"
            variant="light"
            tooltipContent={<AvatarList avatars={overflow} />}
            maxWidth={300}
            preventOverflow={16}
          >
            <div
              className={classNames(
                classes.overflowMenu,
                classes[`overflow-${size}` as const],
                avatarSizeClasses[size],
                classes[`offset-${size}` as const],
              )}
            >
              <Text className={classes.overflowText} appearance={sizeToAppearanceMap[size]}>
                <FormattedMessage
                  defaultMessage="+{number}"
                  id="Z6Uj2U"
                  values={{ number: overflow.length }}
                  description="Represents the number of objects in a list."
                />
              </Text>
            </div>
          </Tooltip>
        )}
      </div>
    );
  },
);

AvatarGroup.displayName = 'AvatarGroup';
